import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MailRegular } from '@fluentui/react-icons';
import { MarkdownContent } from './MarkdownContent';

export function ChatMessage({ 
  message, 
  isStreaming = false, 
  textSizeClasses = {}, 
  onComposeClick, 
  onCitationClick,
  streamingSources,
  deepResearchEnabled = false 
}) {
  const { t, language } = useLanguage();
  const isUser = message.role === 'user';
  
  // Get locale for time formatting based on language
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  
  // Default text size classes if not provided
  const messageTextClass = textSizeClasses.messageText || 'text-sm';
  const avatarSizeClass = textSizeClasses.avatarSize || 'h-8 w-8';
  const timestampTextClass = textSizeClasses.buttonText || 'text-xs';
  const paddingClass = textSizeClasses.padding || 'px-4 py-2';
  
  // Prepare sources for citations
  const messageSources = message.sources || [];
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex gap-3 max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 ${avatarSizeClass} rounded-full flex items-center justify-center ${
          isUser ? 'bg-blue-600 text-white' : 'bg-gray-100'
        }`}>
          {isUser ? (
            <span className={`${messageTextClass} font-medium`}>{t('aiAssistant.you')}</span>
          ) : (
            <img 
              src={deepResearchEnabled ? "../../assets/Ben.png" : "../../assets/Kira.png"}
              alt={deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
              className="h-full w-full rounded-full object-cover"
            />
          )}
        </div>
        
        {/* Message Content */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
          <div className={`rounded-lg ${paddingClass} ${
            isUser 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-900'
          }`}>
            {isUser ? (
              <p className={`${messageTextClass} whitespace-pre-wrap`}>{message.content}</p>
            ) : (
              <div className={messageTextClass}>
                <MarkdownContent 
                  content={message.content} 
                  onCitationClick={onCitationClick ? (citationNumber, event, citationId) => 
                    onCitationClick(citationNumber, event, citationId, messageSources)
                  : undefined}
                  sources={messageSources}
                />
              </div>
            )}
            {isStreaming && !isUser && (
              <span className="inline-block w-1 h-4 bg-gray-600 animate-pulse ml-1"></span>
            )}
          </div>
          
          {/* Compose button for email drafts */}
          {message.showComposeButton && onComposeClick && (
            <button
              onClick={onComposeClick}
              className={`mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 ${messageTextClass}`}
            >
              <MailRegular className="h-4 w-4" />
              {t('aiAssistant.openInOutlook')}
            </button>
          )}
          
          <span className={`${timestampTextClass} text-gray-500 mt-1`}>
            {new Date(message.timestamp).toLocaleTimeString(locale, { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

